import { Component, Host, h, Prop, Watch } from '@stencil/core';
import { DEFAULT_TRACKING_DELAY } from '../../global/utility/tracking-helpers';
import { Link } from '../ui-cms-link/ui-cms-link';

export interface UiCmsFaqEntryBody {
  faqId: string;
  requestUrl: string;
  url?: string;
  answer: string;
  question?: string;
  links?: Link[];
  image?: string;
  imageAltText?: string;
  imageMouseover?: string;
  imageCopyright?: string;
  imageCaptionHeadline?: string;
  imageCaption?: string;
  showImage?: boolean;
  // DEV-NOTE: 'full' option not needed in FAQ compontent
  imageSize?: 'small' | 'medium' | 'large';
  apiKey: string;
  apiVersion: string;
  targetId: string;
  requestUrlPositive: string;
  requestUrlNegative: string;
  requestUrlFeedback: string;
}

@Component({
  tag: 'ui-cms-faq-entry',
  styleUrl: 'ui-cms-faq-entry.scss',
  shadow: false,
})
export class UiCmsFaqEntry {
  @Prop({ attribute: 'body' }) utilityBody: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Prop({ mutable: true }) body: UiCmsFaqEntryBody = {} as any;

  /**
   * When the entry is part of a dropdown, the question should be hidden
   * as it is already shown in the dropdown button.
   */
  @Prop() hideQuestion = false;

  /**
   * The category of which the entry is part of. Currently only used for tracking.
   */
  @Prop() categoryHeadline: string;

  @Prop({ mutable: true }) i18n = {
    thisFAQ: 'diese FAQ',
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
  }

  render() {
    return (
      <Host>
        {this.body.question && !this.hideQuestion && <ui-headline level={1}>{this.body.question}</ui-headline>}
        <div class="ui-cms-faq-entry__answer">
          <ui-cms-text-image-video
            class="ui-cms-faq-entry__answer--long"
            body={{
              headline: '',
              headlineLevel: 1,
              isHeadlineCentered: false,
              isLeadTextAfterMedia: false,
              isTextNotFloating: false,
              mediaSelection: this.body.showImage ? 'image' : 'none',
              image: this.body.image,
              imageAltText: this.body.imageAltText,
              imageMouseover: this.body.imageMouseover,
              imageCopyright: this.body.imageCopyright,
              imageCaptionHeadline: this.body.imageCaptionHeadline,
              imageCaption: this.body.imageCaption,
              imageSize: this.body.imageSize,
              text: this.body.answer,
            }}
          ></ui-cms-text-image-video>
        </div>
        {this.body.links ? (
          <ui-link-list
            links={this.body.links.map((link: Link) => ({
              ...link,
              delay: DEFAULT_TRACKING_DELAY,
            }))}
          ></ui-link-list>
        ) : (
          ''
        )}
        <ui-cms-rating
          requestUrl={this.body.requestUrl}
          itemId={this.body.faqId}
          apiKey={this.body.apiKey}
          apiVersion={this.body.apiVersion}
          requestUrlFeedback={this.body.requestUrlFeedback}
          faq={this.body.question}
          url={this.body.url ?? (window.location.href || 'missing url')}
          targetId={this.body.targetId}
          requestUrlPositive={this.body.requestUrlPositive}
          requestUrlNegative={this.body.requestUrlNegative}
          countItemLabel={this.i18n.thisFAQ}
          feedbackItemLabel={this.i18n.thisFAQ}
          additionalTrackingContent={this.body.question}
          withFeedbackModal
        ></ui-cms-rating>
      </Host>
    );
  }
}
